<script setup lang="ts">
/**
 * Selection UI for Styles / Colors / Brands — body of the "Add styles / colors /
 * brands" modal on Style, and the same UI inside the Style quiz (Brands, Colors).
 * Figma `Style · Selection modals`; quiz notes: "Same selection UI as the
 * Brands / Colors modal on Style".
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairCheckboxIndicator } from '@/ui/primitives/FairCheckbox'
import { FairChip } from '@/ui/primitives/FairChip'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import { useFairCompact } from '@/composables/useFairCompact'
import {
  filterFairProfileV2PickerItems,
  type FairProfileV2PickerItem,
} from './fair-profile-v2-my-style.data'
import {
  FAIR_PROFILE_V2_COLOR_FAMILIES,
  FAIR_PROFILE_V2_POPULAR_BRANDS,
  groupFairProfileV2BrandsAz,
  groupFairProfileV2Colors,
  toggleFairProfileV2Pick,
  type FairProfileV2ColorFamily,
  type FairProfileV2PickerGroup,
} from './fair-profile-v2-selection.data'
import './fair-profile-v2-selection.css'

export type FairProfileV2SelectionKind = 'styles' | 'colors' | 'brands'

interface FairProfileV2SelectionPickerProps {
  kind: FairProfileV2SelectionKind
  modelValue: number[]
  items?: FairProfileV2PickerItem[]
  loading?: boolean
  error?: boolean
}

const props = withDefaults(defineProps<FairProfileV2SelectionPickerProps>(), {
  items: () => [],
  loading: false,
  error: false,
})

const emit = defineEmits<{
  'update:modelValue': [ids: number[]]
  retry: []
}>()

const { t } = useI18n()
const compact = useFairCompact()

const search = ref('')
const family = ref<FairProfileV2ColorFamily | 'all'>('all')
const brandTab = ref<'popular' | 'az'>('popular')

const picked = computed(() => props.modelValue)
const searchable = computed(() => props.kind !== 'styles')
const query = computed(() => search.value.trim())
const found = computed(() => filterFairProfileV2PickerItems(props.items, query.value))

/** Colors: family groups; Brands: Popular (flat) or A–Z groups; a search is always flat. */
const groups = computed<FairProfileV2PickerGroup[]>(() => {
  if (props.kind === 'colors') return groupFairProfileV2Colors(found.value, family.value)
  if (props.kind === 'brands') {
    if (query.value) return [{ id: 'results', items: found.value }]
    if (brandTab.value === 'az') return groupFairProfileV2BrandsAz(found.value)
    return [{ id: 'popular', items: found.value.slice(0, FAIR_PROFILE_V2_POPULAR_BRANDS) }]
  }
  return [{ id: 'styles', items: found.value }]
})

const isEmptyResult = computed(
  () => !props.loading && !props.error && groups.value.every((group) => !group.items.length),
)

function groupLabel(group: FairProfileV2PickerGroup): string {
  if (props.kind === 'colors') return t(`profile.selection.families.${group.id}`)
  if (props.kind === 'brands' && brandTab.value === 'az' && !query.value) return group.id
  return ''
}

function toggle(id: number) {
  emit('update:modelValue', toggleFairProfileV2Pick(props.modelValue, id))
}
</script>

<template>
  <div
    class="fair-profile-v2-selection__body"
    :class="[`fair-profile-v2-selection--${kind}`, { 'fair-profile-v2-selection--compact': compact }]"
  >
    <!-- Search and filters (Colors / Brands) -->
    <div
      v-if="searchable && !error"
      class="fair-profile-v2-selection__tools"
    >
      <FairInputText
        v-model="search"
        variant="underline"
        :placeholder="t(`profile.selection.${kind}.search`)"
      />
      <div
        v-if="kind === 'colors'"
        class="fair-profile-v2-selection__chips"
      >
        <FairChip
          :active="family === 'all'"
          :label="t('profile.selection.families.all')"
          @click="family = 'all'"
        />
        <FairChip
          v-for="key in FAIR_PROFILE_V2_COLOR_FAMILIES"
          :key="key"
          :active="family === key"
          :label="t(`profile.selection.families.${key}`)"
          @click="family = key"
        />
      </div>
      <div
        v-else
        class="fair-profile-v2-selection__chips"
      >
        <FairChip
          :active="brandTab === 'popular' && !query"
          :label="t('profile.selection.brands.popular')"
          @click="brandTab = 'popular'"
        />
        <FairChip
          :active="brandTab === 'az' && !query"
          :label="t('profile.selection.brands.az')"
          @click="brandTab = 'az'"
        />
      </div>
    </div>

    <!-- Load error (D5 / 7) -->
    <div
      v-if="error"
      class="fair-profile-v2-selection__status"
      role="alert"
    >
      <p class="fair-profile-v2-selection__status-text">
        {{ t(`profile.selection.${kind}.load_error`) }}
      </p>
      <button
        type="button"
        class="fair-profile-v2-selection__status-action"
        @click="emit('retry')"
      >
        {{ t('profile.dna.retry') }}
      </button>
    </div>

    <!-- Loading (D4 / 6) -->
    <div
      v-else-if="loading"
      class="fair-profile-v2-selection__grid"
      :class="{ 'fair-profile-v2-selection__grid--rows': kind !== 'styles' }"
      aria-busy="true"
    >
      <template v-if="kind === 'styles'">
        <div
          v-for="index in 8"
          :key="index"
          class="fair-profile-v2-selection__tile"
        >
          <div class="fair-profile-v2-selection__photo">
            <FairSkeleton
              variant="media"
              fill
              :mark="false"
            />
          </div>
          <FairSkeleton
            :width="72"
            :height="10"
          />
        </div>
      </template>
      <template v-else>
        <FairSkeleton
          v-for="index in 12"
          :key="index"
          :height="44"
          radius="var(--radius-control)"
        />
      </template>
    </div>

    <!-- No results (5) -->
    <div
      v-else-if="isEmptyResult && query"
      class="fair-profile-v2-selection__status"
    >
      <p class="fair-profile-v2-selection__status-text">
        {{ t(`profile.selection.${kind}.no_results`, { query }) }}
      </p>
      <button
        type="button"
        class="fair-profile-v2-selection__status-action"
        @click="search = ''"
      >
        {{ t('profile.selection.clear_search') }}
      </button>
    </div>

    <!-- Styles: 3:4 tiles (D1) -->
    <div
      v-else-if="kind === 'styles'"
      class="fair-profile-v2-selection__grid"
    >
      <button
        v-for="item in groups[0]?.items ?? []"
        :key="item.id"
        type="button"
        class="fair-profile-v2-selection__tile"
        :class="{ 'fair-profile-v2-selection__tile--picked': picked.includes(item.id) }"
        :aria-pressed="picked.includes(item.id)"
        @click="toggle(item.id)"
      >
        <span class="fair-profile-v2-selection__photo">
          <img
            v-if="item.image"
            :src="item.image"
            alt=""
            loading="lazy"
          >
          <span
            v-if="picked.includes(item.id)"
            class="fair-profile-v2-selection__badge"
            aria-hidden="true"
          >
            <FairIcon
              name="check"
              :size="12"
            />
          </span>
        </span>
        <span class="fair-profile-v2-selection__name">{{ item.name }}</span>
      </button>
    </div>

    <!-- Colors / Brands: MenuRow (Multiselect) lists -->
    <div
      v-else
      class="fair-profile-v2-selection__list"
    >
      <section
        v-for="group in groups"
        :key="group.id"
        class="fair-profile-v2-selection__group"
      >
        <p
          v-if="groupLabel(group)"
          class="fair-profile-v2-selection__group-label"
        >
          {{ groupLabel(group) }}
        </p>
        <div class="fair-profile-v2-selection__rows">
          <button
            v-for="item in group.items"
            :key="item.id"
            type="button"
            role="checkbox"
            class="fair-profile-v2-selection__row"
            :aria-checked="picked.includes(item.id)"
            @click="toggle(item.id)"
          >
            <span class="fair-profile-v2-selection__row-label">
              <span
                v-if="kind === 'colors'"
                class="fair-profile-v2-selection__swatch"
                :style="{ background: item.hex }"
                aria-hidden="true"
              />
              <span class="fair-profile-v2-selection__row-name">{{ item.name }}</span>
            </span>
            <span class="fair-profile-v2-selection__check">
              <FairCheckboxIndicator :checked="picked.includes(item.id)" />
            </span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>
